import { useSetupFooter } from "views/Setup/context/SetupFooterContext";
import CreateUpdateBankAccount from "../Bank/CreateUpdateBankAccount";
import PaymentDateLimit from "../PaymentDateLimit/PaymentDateLimit";
import { usePaymentForm } from "../../hooks/usePaymentForm";
import { useLocation, useParams } from "react-router-dom";
import useFooterStore from "views/hooks/useFooterStore";
import CreateUpdateBank from "../Bank/CreateUpdateBank";
import { useBankData } from "../../hooks/useBankData";
import { Form, Row, Col, Card, Select } from "antd";
import { ApolloQueryResult } from "@apollo/client";
import { PaymentMethods } from "./PaymentMethods";
import { useTranslation } from "react-i18next";
import CardTitle from "components/CardTitle";
import { FaLandmark } from "react-icons/fa";
import React, { useEffect } from "react";
import Loading from "components/Loading";

interface PaymentPropsTypes {
  nextButton?: () => void;
  prevButton?: () => void;
  isSetup?: boolean;
  loading?: boolean;
  refetch?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetCompanySettingResponse>>;
}

export default function Payment(props: PaymentPropsTypes): React.ReactElement {
  const { nextButton, isSetup, loading, refetch } = props;
  const location = useLocation();
  const { t } = useTranslation();
  const urlParams = useParams();
  const [form] = Form.useForm();

  const tabName = location.pathname.split("/").pop();

  const handleOkSaveButton = useFooterStore(state => state.handleOkSaveButton);
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);

  const { onFinish, onFinishFailed, handleOk } = usePaymentForm(nextButton, isSetup, form, refetch);

  const {
    data,
    banksOptions,
    refetchListBanks,
    refetchCompanyBanks,
    paymentMethodsOptions,
    listCompaniesBankAccountsPaginated,
  } = useBankData({ form });

  const defaultBankAccountId = Form.useWatch(["defaultBankAccountId"], form);
  const defaultBank = Form.useWatch(["defaultBankInstitutionId"], form);

  useEffect(() => {
    if (defaultBank && urlParams?.companyId) {
      listCompaniesBankAccountsPaginated({
        variables: {
          filter: {
            companyId: urlParams?.companyId,
            bankId_In: [defaultBank],
          },
        },
      });
    }
  }, [defaultBank, urlParams?.companyId]);

  useEffect(() => {
    if (handleOkSaveButton && tabName === "payment-settings") {
      handleOk();
    }
  }, [handleOkSaveButton]);

  const onValuesChange = (changedValues: Record<string, any>) => {
    if (changedValues?.defaultBankInstitutionId) {
      form.resetFields(["defaultBankAccountId"]);
    }
  };

  const handleSaveAndNext = () => {
    setSaveLoading(true);
    form.submit();
  };

  useSetupFooter(isSetup ? { onSave: handleSaveAndNext, loading } : null);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <Form
          name="updateCompany"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <Card
                    title={<CardTitle icon={<FaLandmark />} title={t("Bank")} />}
                    className="antd-newrai-card"
                  >
                    <Row gutter={[8, 8]}>
                      <Col xs={24} sm={21}>
                        <Form.Item label={t("Bank")} name="defaultBankInstitutionId">
                          <Select options={banksOptions} />
                        </Form.Item>
                      </Col>
                      <Col span={3} className="centered">
                        <CreateUpdateBank id={defaultBank} refetchListBanks={refetchListBanks} />
                      </Col>
                    </Row>
                    <Row gutter={[8, 8]}>
                      <Col xs={24} sm={21}>
                        <Form.Item name="defaultBankAccountId" label={t("Default bank account")}>
                          <Select
                            disabled={!defaultBank}
                            options={data?.map(item => ({
                              label: item.name,
                              value: item.id,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={3} className="centered">
                        <CreateUpdateBankAccount
                          id={defaultBankAccountId}
                          bankId={defaultBank}
                          refetchCompanyBanks={refetchCompanyBanks}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col span={24}>
                  <PaymentMethods paymentMethodsOptions={paymentMethodsOptions} isSetup={isSetup} />
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={12}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24}>
                  <PaymentDateLimit />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      )}
    </>
  );
}
